import { useApi } from '../../core/api/api.context';
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from 'react-query';
import { QueryKeys } from '../../core/queries/query-keys.constants';
import {
  KnownSortOrder,
  DeliveriesControllerDeliveriesListOptionalParams,
  CreateDeliveryDto,
  DeliveryDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { CustomDeliveryStateOptions, OptionsToValuesMap } from './deliveries.constants';
import { Delivery } from './models/delivery.model';
import { RestError } from '@azure/core-http';
type MaterialUiPickersDate = import('dayjs').Dayjs | null;

export type UseQueryListDeliveriesParams = {
  searchTerm?: string;
  state?: string;
  deliveryType?: string;
  deliveredFrom: MaterialUiPickersDate | null;
  deliveredTo: MaterialUiPickersDate | null;
  createdFrom: MaterialUiPickersDate | null;
  createdTo: MaterialUiPickersDate | null;
  sortBy: string;
  sortOrder: KnownSortOrder;
  userID?: string;
  hubID?: string | null;
  carrierID: string | null;
};

function serializeQueryParams({
  deliveredFrom,
  deliveredTo,
  createdFrom,
  createdTo,
  sortBy,
  sortOrder,
  state,
  deliveryType,
  searchTerm,
  userID,
  hubID,
  carrierID
}: Partial<UseQueryListDeliveriesParams>): DeliveriesControllerDeliveriesListOptionalParams {
  return {
    deliveredTo: deliveredTo ? deliveredTo.toDate() : undefined,
    deliveredFrom: deliveredFrom ? deliveredFrom.toDate() : undefined,
    createdTo: createdTo ? createdTo.toDate() : undefined,
    createdFrom: createdFrom ? createdFrom.toDate() : undefined,
    states:
      state && (state === 'not-delivered' || state === 'completed' || state === 'current-deliveries-category')
        ? OptionsToValuesMap[state as CustomDeliveryStateOptions]
        : state
        ? [state]
        : undefined,
    state: undefined,
    deliveryType,
    searchTerm: parseSearchTerm(searchTerm),
    sortBy,
    sortOrder,
    userID,
    hubID: hubID || undefined,
    carrierIDs: carrierID ? [carrierID] : undefined
  };
}

function parseSearchTerm(searchTerm?: string) {
  if (!searchTerm) {
    return undefined;
  }
  const cleanSearchTerm = searchTerm
    .split(' ')
    .filter((word) => word.length > 0)
    .join(' ');
  // The search term must be at least 5 characters long
  // It must not match or be a substring of any of the restricted keywords
  const restrictedKeywords = [
    'australia post',
    'amazon flex',
    'couriers please',
    'please',
    'aramax',
    'startrack',
    'dhl express',
    'express',
    'campus living villages',
    'villages',
    'team global express',
    'imile',
    'scape',
    'fedex',
    'codex'
  ];

  if (
    cleanSearchTerm.length < 5 ||
    restrictedKeywords.some((keyword) => keyword.startsWith(cleanSearchTerm.toLowerCase()))
  ) {
    return undefined;
  }

  return cleanSearchTerm;
}

export function useInfiniteQueryDeliveriesList(
  projectID: string | undefined,
  page: number,
  params: Partial<UseQueryListDeliveriesParams>,
  // Defaults to requiring a project. Pass true to list across projects, e.g. by userID alone.
  enabled = projectID !== undefined
) {
  const api = useApi();
  const queryParams = serializeQueryParams(params);

  return useInfiniteQuery(
    [QueryKeys.DELIVERIES, projectID, page, queryParams],
    async ({ pageParam = 1 }) => {
      if (!enabled) {
        return {
          items: [],
          totalRecords: 0,
          perPageRecords: 25,
          totalPages: 1,
          pageNo: 1
        };
      }
      const result = await api.deliveriesController.deliveriesList({
        ...queryParams,
        projectID,
        includeChildProjects: projectID !== undefined ? true : undefined,
        includeNotifications: true,
        maxNotifications: 1,
        pageNo: pageParam,
        perPageRecords: 25
      });

      return { ...result, items: (result.items || []).map((d) => new Delivery(d)) };
    },
    {
      enabled,
      getNextPageParam(lastPage) {
        if (lastPage.pageNo < lastPage.totalPages) {
          return lastPage.pageNo + 1;
        } else {
          return undefined;
        }
      }
    }
  );
}

export const useQueryGetDeliveries = (ids: string[]) => {
  const api = useApi();
  const queries = ids.map((id) => ({
    queryKey: [QueryKeys.DELIVERIES, id],
    queryFn: () => api.deliveriesController.deliveriesGet(id)
  }));
  return useQueries(queries);
};

export function useQueryListPartiesByReceivingProject(projectID: string, transactionType: string) {
  const api = useApi();

  return useQuery([QueryKeys.PARTIES, projectID, transactionType], async () => {
    if (!projectID || !transactionType) return [];
    return await api.partiesController.list(projectID, transactionType);
  });
}

export function useMutationCreateDelivery() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<DeliveryDto, RestError, CreateDeliveryDto>(
    async (dto: CreateDeliveryDto) => {
      return await api.deliveriesActionsController.createDelivery(dto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(QueryKeys.DELIVERIES);
      }
    }
  );
}
